/**
 * BannerPositionDetail - Redesigned Banner Position Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	SwitchBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageSection, EditPageField } from '../../components/container';
import { BannerPositionModel, dfBannerPositionModel, validBannerPositionSchema } from '../../context/models/banners/BannerPositionModel';
import { MapPin, Ruler, Settings, Info } from 'lucide-react';

type IBannerPositionDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: BannerPositionModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: BannerPositionModel) => void;
};

const BannerPositionDetail = (props: IBannerPositionDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<BannerPositionModel>(dfBannerPositionModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<BannerPositionModel>({
		resolver: yupResolver(validBannerPositionSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: BannerPositionModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof BannerPositionModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Position Info
	const positionTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Tên vị trí'
				required
				error={errors.name?.message}
				helpText='Tên hiển thị của vị trí banner'
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên vị trí...'
				/>
			</EditPageField>

			<EditPageField
				label='Mã vị trí'
				required
				error={errors.code?.message}
				helpText='Mã định danh duy nhất cho vị trí'
			>
				<TextBox
					name='code'
					error={errors.code?.message}
					onValueChanged={(value) => updateField('code', value)}
					value={initialValues.code}
					hasValid={true}
					placeholder='BANNER_01'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Dimensions
	const dimensionsTab = (
		<div className='space-y-4'>
			<EditPageSection
				title='Kích thước Banner'
				icon={<Ruler className='h-4 w-4' />}
			>
				<div className='grid grid-cols-2 gap-4'>
					<EditPageField
						label='Chiều rộng (px)'
						required
						error={errors.width?.message}
					>
						<NumberBox
							name='width'
							error={errors.width?.message}
							onValueChanged={(value) => updateField('width', value)}
							value={initialValues.width}
							hasValid={true}
						/>
					</EditPageField>

					<EditPageField
						label='Chiều cao (px)'
						required
						error={errors.height?.message}
					>
						<NumberBox
							name='height'
							error={errors.height?.message}
							onValueChanged={(value) => updateField('height', value)}
							value={initialValues.height}
							hasValid={true}
						/>
					</EditPageField>
				</div>
			</EditPageSection>
		</div>
	);

	// Tab content: Status
	const statusTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Trạng thái hoạt động'
			>
				<SwitchBox
					name='inActive'
					label='Áp dụng vị trí banner này'
					error={errors.inActive?.message}
					onValueChanged={(value) => updateField('inActive', value)}
					value={initialValues.inActive}
					sizeClass='sm'
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'position',
			label: 'Thông tin vị trí',
			icon: <MapPin className='h-4 w-4' />,
			content: positionTab,
		},
		{
			id: 'dimensions',
			label: 'Kích thước',
			icon: <Ruler className='h-4 w-4' />,
			content: dimensionsTab,
		},
		{
			id: 'status',
			label: 'Trạng thái',
			icon: <Settings className='h-4 w-4' />,
			content: statusTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới vị trí banner' : 'Chỉnh sửa vị trí banner'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Vị trí Banner', href: '/ads-position' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default BannerPositionDetail;
